import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../hooks/useOnSaved';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as parentOrganizationsSlice from '../../../data/parent-organizations.slice';
import { useTranslation } from 'react-i18next';
import clientUtils from '../../../services/clientUtils';
import { Client, ClientAddress } from '../../../data/models/client.model';
import SelectMenu from '../../forms/SelectMenu';
import AddressInput from '../../forms/AddressInput';

const PREFIX = 'AddressDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    position: 'absolute',
    top: 64,
  },
  '& .MuiButtonBase-root': {
    fontSize: '15px !important',
  },

  '& .MuiButton-root': {
    padding: theme.spacing(1, 1.75),
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    width: 380,
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface AddressDialogProps {
  title: string;
  open: boolean;
  client: Client;
  address?: ClientAddress;
  close: () => void;
}

export default function AddressDialog({
  title,
  open,
  client,
  address,
  close,
}: AddressDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const savedAddress = useAppSelector(clientSlice.selectSavedAddress);
  const clientAddressUses = useAppSelector(lookupSlice.selectClientAddressUses);
  const savingClient = useAppSelector(clientSlice.selectSavingClient);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getAddressDefaults(address),
  });

  const enteredAddress = formHook.watch('Address');

  React.useEffect(() => {
    const defaults = clientUtils.getAddressDefaults(address);
    formHook.reset(defaults);
  }, [address]);

  React.useEffect(() => {
    dispatch(parentOrganizationsSlice.fetchParentOrganizationsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('clientVertical'));
  }, []);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  useOnSaved(savedAddress, handleClose);

  const handleSave = (data: any) => {
    const newAddress = clientUtils.getAddressFromFormData(data, address?.state);
    if (address) {
      dispatch(
        clientSlice.updateClientAddressAsync(client.clientBC, {
          clientAddressUUID: address.clientAddressUUID,
          ...newAddress,
        }),
      );
    } else {
      dispatch(
        clientSlice.createClientAddressAsync(client.clientBC, newAddress),
      );
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {title}
        {savingClient && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <SelectMenu
            label={t('client.addressUse')}
            name="AddressUse"
            values={clientAddressUses}
            valueAttr="clientAddressUseBC"
            labelAttr="clientAddressUseName"
            required={true}
            control={formHook.control}
            rules={{ required: requiredFieldMessage }}
          />
          <AddressInput
            name="Address"
            label={t('client.address')}
            required={true}
            watch={formHook.watch}
            setValue={formHook.setValue}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid || !enteredAddress || savingClient
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
